const RENDER_TYPE = "render";
const IMPRESSION_TYPE = "impression";
const CLICK_TYPE = "click";
const CLICK_IMPRESSION_TYPE = "clickImpression";
const CLICKTHROUGH_EVENT = "clickthrough";

export default {
  namespaced: true,

  state: () => ({
    placement: "other",
    seed: [],
    products: null,
    responseIds: null,
    visibleProductIds: null,
    fetchContextByTag: null,
  }),

  getters: {
    getProducts(state) {
      return tag => state.products?.[tag] ?? [];
    },
    getResponseId(state) {
      return tag => state.responseIds?.[tag] ?? null;
    },
    getVisibleProductIds(state) {
      return tag => state.visibleProductIds?.[tag] ?? [];
    },
    toBeaconProduct(state, getters) {
      return (productId, tag) => {
        const product = getters
          .getProducts(tag)
          .find(p => p.id == productId || p.recId == productId);
        const mappings = product?.mappings?.core;
        if (!mappings) return null;
        const uid = mappings.uid;
        const sku = mappings.sku;
        return {
          type: "product",
          parentId: uid,
          uid,
          ...(sku && { sku }),
        };
      };
    },
  },

  actions: {
    init({ commit }, { placement, seed }) {
      commit("init", { placement, seed });
    },
    async fetchProducts(
      { commit, getters, state },
      { tag, limit, sku, skipParams } = {}
    ) {
      const products = getters.getProducts(tag);
      const lastSku = state.fetchContextByTag?.[tag]?.sku ?? null;
      if (products.length > 0 && lastSku === (sku ?? null)) {
        return;
      }

      const { data } = await this.$search.recommend({
        tag,
        limit,
        sku,
        skipParams,
      });

      commit("setProducts", { tag, products: data.results });
      if (data.responseId) {
        commit("setResponseId", { tag, responseId: data.responseId });
      }
      commit("setFetchContext", { tag, sku: sku ?? null });
    },
    setVisibleProductsIds({ commit }, { tag, productId }) {
      commit("setVisibleProductsIds", { tag, productId });
    },
    async trackBeacon({ state, commit, getters }, { type, productId, tag }) {
      const responseId = getters.getResponseId(tag);
      if (!responseId) {
        return;
      }

      const toResults = productIds => {
        return productIds
          .map(pid => getters.toBeaconProduct(pid, tag))
          .filter(Boolean);
      };

      if (type === RENDER_TYPE) {
        await this.$search.callBeacon2Api("recommendations", RENDER_TYPE, {
          tag,
          responseId,
        });
      }

      if (type === IMPRESSION_TYPE) {
        const visibleIds = getters.getVisibleProductIds(tag);
        const results = toResults(visibleIds);
        if (results.length > 0) {
          await this.$search.callBeacon2Api(
            "recommendations",
            IMPRESSION_TYPE,
            {
              tag,
              responseId,
              results,
              banners: [],
            }
          );
        }
        commit("clearVisibleProductIds", { tag });
      }

      if (type === CLICK_TYPE && productId) {
        const product = getters.toBeaconProduct(productId, tag);
        if (product) {
          await this.$search.callBeacon2Api(
            "recommendations",
            CLICKTHROUGH_EVENT,
            {
              tag,
              responseId,
              results: [product],
            }
          );
        }
      }

      if (type === CLICK_IMPRESSION_TYPE) {
        const visibleIds = getters.getVisibleProductIds(tag);
        const results = toResults(visibleIds);
        if (results.length > 0) {
          await this.$search.callBeacon2Api(
            "recommendations",
            IMPRESSION_TYPE,
            {
              tag,
              responseId,
              results,
              banners: [],
            }
          );
        }
        commit("clearVisibleProductIds", { tag });
      }
    },
  },

  mutations: {
    init(state, { placement, seed }) {
      state.placement = placement;
      state.seed = seed;
      state.products = null;
      state.responseIds = null;
      state.visibleProductIds = null;
      state.fetchContextByTag = null;
    },
    setProducts(state, { tag, products }) {
      state.products = {
        ...state.products,
        [tag]: products,
      };
    },
    setResponseId(state, { tag, responseId }) {
      state.responseIds = {
        ...state.responseIds,
        [tag]: responseId,
      };
    },
    setVisibleProductsIds(state, { tag, productId }) {
      const productIds = [...(state.visibleProductIds?.[tag] ?? []), productId];

      state.visibleProductIds = {
        ...state.visibleProductIds,
        [tag]: productIds,
      };
    },
    clearVisibleProductIds(state, { tag }) {
      state.visibleProductIds = {
        ...state.visibleProductIds,
        [tag]: [],
      };
    },
    setFetchContext(state, { tag, sku }) {
      state.fetchContextByTag = {
        ...state.fetchContextByTag,
        [tag]: { sku },
      };
    },
  },
};
